import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

export default class extends Controller {
  static targets = ["odometer", "kpis", "zones", "sources", "timestamp"]
  static values = {
    slug: String,
    interval: { type: Number, default: 60000 }  // Default 1 minute fallback
  }

  connect() {
    this.currentValue = this.getOdometerValue()

    // Initialize odometer immediately, then update after fonts load
    this.initializeOdometer()

    // Re-initialize after fonts load to fix any positioning issues
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        setTimeout(() => this.initializeOdometer(), 50)
      })
    }

    // Render existing KPI/bar values with tabular digits
    this.initTabularDigits()

    this.subscribeToChannel()
    this.startAutoRefresh()  // Fallback polling

    // Handle resize for responsive odometer
    this.boundHandleResize = this.handleResize.bind(this)
    window.addEventListener('resize', this.boundHandleResize)
  }

  subscribeToChannel() {
    this.subscription = consumer.subscriptions.create(
      { channel: "DashboardChannel", slug: this.slugValue },
      {
        received: (data) => {
          console.log("Dashboard update received:", data)
          this.updateDashboard(data)
        },
        connected: () => {
          console.log("Connected to DashboardChannel")
        },
        disconnected: () => {
          console.log("Disconnected from DashboardChannel")
        }
      }
    )
  }

  handleResize() {
    // Debounce resize handling
    clearTimeout(this.resizeTimeout)
    this.resizeTimeout = setTimeout(() => {
      this.initializeOdometer()
    }, 100)
  }

  initializeOdometer() {
    if (!this.hasOdometerTarget) return

    const digitHeight = this.getDigitHeight()
    // Support both old (.odometer-wheel) and new (.mwc-digit-wheel) classes
    const wheels = this.odometerTarget.querySelectorAll('.odometer-wheel, .mwc-digit-wheel')

    wheels.forEach(wheel => {
      const digit = parseInt(wheel.dataset.digit) || 0
      wheel.style.transform = `translateY(-${digit * digitHeight}px)`
    })
  }

  disconnect() {
    this.stopAutoRefresh()
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
    window.removeEventListener('resize', this.boundHandleResize)
    clearTimeout(this.resizeTimeout)
  }

  startAutoRefresh() {
    this.refreshTimer = setInterval(() => {
      this.fetchData()
    }, this.intervalValue)
  }

  stopAutoRefresh() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
    }
  }

  getOdometerValue() {
    if (!this.hasOdometerTarget) return 0
    return parseInt(this.odometerTarget.dataset.value) || 0
  }

  async fetchData() {
    try {
      const response = await fetch(`/e/${this.slugValue}/data`, {
        headers: {
          'Accept': 'application/json'
        }
      })

      if (!response.ok) return

      const data = await response.json()
      this.updateDashboard(data)
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
    }
  }

  updateDashboard(data) {
    // Animate odometer
    if (data.total_liters !== undefined) {
      this.animateOdometer(Math.floor(data.total_liters))
    }

    // Update KPIs
    if (data.kpis && this.hasKpisTarget) {
      this.updateKpis(data.kpis, data.total_liters)
    }

    // Update zones
    if (data.zones && this.hasZonesTarget) {
      this.updateZones(data.zones)
    }

    // Update sources (by_source template)
    if (data.sources && this.hasSourcesTarget) {
      this.updateSources(data.sources)
    }

    // Update timestamp
    if (this.hasTimestampTarget && data.updated_at) {
      const date = new Date(data.updated_at)
      this.timestampTarget.textContent = date.toLocaleTimeString()
    }
  }

  getDigitHeight() {
    // Get the actual height of a digit-number element (the container for each number)
    // This is more reliable than getting the digit container height
    const digitNumber = this.odometerTarget?.querySelector('.mwc-digit-number, .odometer-number')
    if (digitNumber) {
      return digitNumber.offsetHeight
    }
    // Fallback to digit container
    const digit = this.odometerTarget?.querySelector('.odometer-digit, .mwc-digit')
    return digit ? digit.offsetHeight : 100
  }

  animateOdometer(targetValue) {
    if (!this.hasOdometerTarget) return

    const startValue = this.currentValue
    if (targetValue === startValue) return

    // Detect if using MWC style (no thousand separators)
    const isMwcStyle = this.odometerTarget.classList.contains('mwc-odometer')

    // Format the new value
    const formattedTarget = isMwcStyle ? targetValue.toString() : this.formatNumberWithDots(targetValue)
    const formattedStart = isMwcStyle ? startValue.toString() : this.formatNumberWithDots(startValue)

    // Rebuild odometer if digit count changed
    if (formattedTarget.length !== formattedStart.length) {
      this.rebuildOdometer(targetValue)
      this.currentValue = targetValue
      this.odometerTarget.dataset.value = targetValue
      return
    }

    // Get current digit height (responsive)
    const digitHeight = this.getDigitHeight()

    // Animate each digit with staggered delays
    // Support both old (.odometer-digit) and new (.mwc-digit) classes
    const digits = this.odometerTarget.querySelectorAll('.odometer-digit, .mwc-digit')
    const targetChars = formattedTarget.split('')
    const startChars = formattedStart.split('')

    let digitIndex = 0
    targetChars.forEach((char, index) => {
      if (char === '.') return // Skip separators

      const digit = digits[digitIndex]
      if (!digit) {
        digitIndex++
        return
      }

      const wheel = digit.querySelector('.odometer-wheel, .mwc-digit-wheel')
      const currentDigit = parseInt(startChars[index]) || 0
      const targetDigit = parseInt(char) || 0

      if (currentDigit !== targetDigit) {
        // Add random delay for natural effect (0-500ms)
        const delay = Math.random() * 500

        setTimeout(() => {
          wheel.style.transform = `translateY(-${targetDigit * digitHeight}px)`
          wheel.dataset.digit = targetDigit
        }, delay)
      }

      digitIndex++
    })

    this.currentValue = targetValue
    this.odometerTarget.dataset.value = targetValue
  }

  rebuildOdometer(value) {
    // Detect if using MWC style (no thousand separators, just digits)
    const isMwcStyle = this.odometerTarget.classList.contains('mwc-odometer')
    const formatted = isMwcStyle ? value.toString() : this.formatNumberWithDots(value)
    const digitHeight = this.getDigitHeight()
    let html = ''

    if (isMwcStyle) {
      // MWC style: plain digits without separators
      formatted.split('').forEach((char, index) => {
        const digit = parseInt(char) || 0
        html += `
          <div class="mwc-digit" data-digit-index="${index}">
            <div class="mwc-digit-wheel" data-digit="${digit}" style="transform: translateY(-${digit * digitHeight}px);">
              ${[0,1,2,3,4,5,6,7,8,9].map(n => `<div class="mwc-digit-number">${n}</div>`).join('')}
            </div>
          </div>
        `
      })
    } else {
      // Old style: with thousand separators
      formatted.split('').forEach((char, index) => {
        if (char === '.') {
          html += '<div class="odometer-separator">.</div>'
        } else {
          const digit = parseInt(char) || 0
          html += `
            <div class="odometer-digit" data-digit-index="${index}">
              <div class="odometer-wheel" data-digit="${digit}" style="transform: translateY(-${digit * digitHeight}px);">
                ${[0,1,2,3,4,5,6,7,8,9].map(n => `<div class="odometer-number">${n}</div>`).join('')}
              </div>
            </div>
          `
        }
      })
    }

    this.odometerTarget.innerHTML = html
  }

  updateKpis(kpis, totalLiters) {
    // Update liters KPI
    const litersEl = this.kpisTarget.querySelector('[data-kpi="liters"]')
    if (litersEl && totalLiters !== undefined) {
      this.animateKpiValue(litersEl, Math.round(totalLiters))
    }

    // Update carbon KPI
    const carbonEl = this.kpisTarget.querySelector('[data-kpi="carbon"]')
    if (carbonEl && kpis.carbon_saved !== undefined) {
      this.animateKpiValue(carbonEl, Math.round(kpis.carbon_saved))
    }

    // Update plastic KPI
    const plasticEl = this.kpisTarget.querySelector('[data-kpi="plastic"]')
    if (plasticEl && kpis.plastic_saved !== undefined) {
      this.animateKpiValue(plasticEl, Math.round(kpis.plastic_saved))
    }

    // Update water footprint KPI
    const waterEl = this.kpisTarget.querySelector('[data-kpi="water"]')
    if (waterEl && kpis.water_footprint_saved !== undefined) {
      this.animateKpiValue(waterEl, Math.round(kpis.water_footprint_saved))
    }
  }

  animateKpiValue(element, targetValue) {
    const currentValue = this.parseFormattedNumber(element.textContent)
    if (currentValue === targetValue) return

    const duration = 1500 // 1.5 seconds
    const steps = 30
    const stepDuration = duration / steps
    const stepValue = (targetValue - currentValue) / steps

    let step = 0
    const animate = () => {
      step++
      const currentStep = currentValue + (stepValue * step)

      if (step < steps) {
        this.renderTabularDigits(element, this.formatNumberWithDots(Math.round(currentStep)))
        setTimeout(animate, stepDuration)
      } else {
        this.renderTabularDigits(element, this.formatNumberWithDots(targetValue))
      }
    }

    animate()
  }

  // Convert existing KPI and bar values to tabular digit spans
  initTabularDigits() {
    const selectors = '.mwc-kpi-value, .mwc-bar-value'
    this.element.querySelectorAll(selectors).forEach(el => {
      this.renderTabularDigits(el, el.textContent.trim())
    })
  }

  // Wrap each character in a fixed-width span for uniform digit widths
  renderTabularDigits(element, text) {
    element.innerHTML = text.split('').map(char => {
      if (/\d/.test(char)) {
        return `<span class="td">${char}</span>`
      }
      return `<span class="ts">${char}</span>`
    }).join('')
  }

  updateZones(zones) {
    // Find max value for percentage calculation
    const maxLiters = Math.max(...zones.map(z => z.liters), 1)
    // Round up to nice number for chart (must match Ruby implementation)
    const chartMax = this.niceMax(maxLiters)

    // Update Y-axis labels if they exist
    const yAxis = this.zonesTarget.querySelector('.mwc-chart-yaxis')
    if (yAxis) {
      const labels = yAxis.querySelectorAll('span')
      if (labels.length >= 5) {
        labels[0].textContent = this.formatNumberWithDots(chartMax)
        labels[1].textContent = this.formatNumberWithDots(Math.floor(chartMax * 0.75))
        labels[2].textContent = this.formatNumberWithDots(Math.floor(chartMax * 0.5))
        labels[3].textContent = this.formatNumberWithDots(Math.floor(chartMax * 0.25))
        labels[4].textContent = '0'
      }
    }

    zones.forEach(zone => {
      // Update value text - check both old and MWC selectors
      const valueEl = this.zonesTarget.querySelector(`[data-zone-liters="${zone.id}"]`) ||
                      this.zonesTarget.querySelector(`[data-zone-id="${zone.id}"] .mwc-bar-value`)
      if (valueEl) {
        this.renderTabularDigits(valueEl, this.formatNumberWithDots(Math.round(zone.liters)))
      }

      // Update bar height
      const barEl = this.zonesTarget.querySelector(`[data-zone-bar="${zone.id}"]`)
      if (barEl) {
        const percentage = (zone.liters / chartMax * 100).toFixed(1)
        barEl.style.height = `${percentage}%`
      }
    })

    // Update interactive map tooltips if present
    const mapElement = document.querySelector('[data-controller="interactive-map"]')
    if (mapElement) {
      const mapController = this.application.getControllerForElementAndIdentifier(mapElement, 'interactive-map')
      if (mapController && mapController.updateZones) {
        mapController.updateZones(zones)
      }
    }
  }

  updateSources(sources) {
    sources.forEach(source => {
      const card = this.sourcesTarget.querySelector(`[data-source-id="${source.id}"]`)
      if (!card) return

      const valueEl = card.querySelector('.mwc-source-value')
      if (valueEl) {
        this.renderTabularDigits(valueEl, this.formatNumberWithDots(Math.round(source.liters)))
      }
    })
  }

  niceMax(value) {
    // Must match the Ruby implementation in dashboards/show.html.erb
    if (value <= 500) return 500
    if (value <= 1000) return 1000
    if (value <= 1500) return 1500
    if (value <= 2000) return 2000
    const step = 500
    return Math.ceil(value / step) * step
  }

  parseFormattedNumber(str) {
    // Parse number formatted with commas as thousand separators
    return parseInt(str.replace(/,/g, '')) || 0
  }

  formatNumberWithDots(num) {
    // Format number with commas as thousand separators (English format)
    return num.toLocaleString('en-US', { maximumFractionDigits: 0 })
  }
}
